import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import {
  HiOutlineCreditCard,
  HiOutlineDocumentText,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineExternalLink,
  HiOutlineDownload,
  HiOutlineRefresh,
} from "react-icons/hi";
import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";

const PLAN_LABELS = {
  test: "Test",
  basico: "Básico",
  estandar: "Estándar",
  premium: "Premium",
};

const STATUS_LABELS = {
  active:              { label: "Activa",       color: "bg-emerald-100 text-emerald-700" },
  trialing:            { label: "Prueba",        color: "bg-sky-100 text-sky-700" },
  past_due:            { label: "Pago pendiente",color: "bg-amber-100 text-amber-700" },
  canceled:            { label: "Cancelada",     color: "bg-gray-200 text-gray-600" },
  unpaid:              { label: "Impagada",      color: "bg-red-100 text-red-700" },
  incomplete:          { label: "Incompleta",    color: "bg-amber-100 text-amber-700" },
  incomplete_expired:  { label: "Expirada",      color: "bg-red-100 text-red-700" },
};

function fmtDate(ts) {
  if (!ts) return "—";
  return new Date(Number(ts) * 1000).toLocaleDateString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

function fmtDatetime(ts) {
  if (!ts) return "—";
  return new Date(Number(ts) * 1000).toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtCurrency(amount, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency", currency, minimumFractionDigits: 2,
  }).format(Number(amount || 0));
}

function fmtPeriod(start, end) {
  if (!start && !end) return "—";
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status || "—", color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function InvoiceTable({ rows, emptyText }) {
  if (!rows.length) {
    return (
      <div className="py-10 text-center text-sm text-gray-400">{emptyText}</div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <TableHead>
          <Th>Fecha</Th>
          <Th>N° Factura</Th>
          <Th>Período</Th>
          <Th align="right">Total</Th>
          <Th align="center">Estado</Th>
          <Th align="center">Acciones</Th>
        </TableHead>
        <tbody>
          {rows.map((inv) => (
            <Tr key={inv.id}>
              <Td className="whitespace-nowrap">{fmtDate(inv.created)}</Td>
              <Td className="whitespace-nowrap font-mono text-xs text-[var(--muted-foreground)]">{inv.number || "—"}</Td>
              <Td className="whitespace-nowrap text-xs text-[var(--muted-foreground)]">{fmtPeriod(inv.period_start, inv.period_end)}</Td>
              <Td align="right" className="whitespace-nowrap font-semibold text-[var(--foreground)]">
                {fmtCurrency(inv.total, inv.currency)}
              </Td>
              <Td align="center" className="whitespace-nowrap">
                <StatusBadge status={inv.status} />
              </Td>
              <Td align="center" className="whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  {inv.hosted_invoice_url && (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ver factura"
                      className="rounded-sm p-1.5 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                    >
                      <HiOutlineExternalLink size={16} />
                    </a>
                  )}
                  {inv.invoice_pdf && (
                    <a
                      href={inv.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Descargar PDF"
                      className="rounded-sm p-1.5 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                    >
                      <HiOutlineDownload size={16} />
                    </a>
                  )}
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FacturacionPage() {
  const [token, , , venueID, venueName] = useContext(UserContext);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch(`/v1/billing/invoices?venue_id=${venueID}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Error cargando facturas");
    } finally {
      setLoading(false);
    }
  }, [token, venueID, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sub        = data?.subscription;
  const venue      = data?.venue;
  const pending    = data?.pending || [];
  const paid       = data?.paid    || [];
  const planLabel  = PLAN_LABELS[venue?.plan_tier] || "—";

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      {/* Hero header */}
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(132,204,22,0.22),_transparent_40%),linear-gradient(135deg,_#0f172a,_#14532d_55%,_#ecfccb)] p-6 text-white shadow-lg flex items-center gap-4">
        <div className="rounded-2xl bg-white/10 p-3 shrink-0">
          <HiOutlineCreditCard size={28} className="text-lime-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-lime-200">Mi suscripción</div>
          <h1 className="mt-0.5 text-2xl font-bold">Facturación ClubsAgent</h1>
          <p className="mt-0.5 text-sm text-lime-50 opacity-80">
            Historial de pagos y facturas de tu licencia de uso.
            {venueName ? ` · ${venueName}` : ""}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="shrink-0 rounded-xl bg-white/10 p-2.5 hover:bg-white/20 transition-colors"
          title="Actualizar"
        >
          <HiOutlineRefresh size={18} className="text-lime-200" />
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {/* Subscription status card */}
      {!loading && venue && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-lime-50 p-2.5">
                <HiOutlineDocumentText size={22} className="text-lime-600" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-400">Plan activo</div>
                <div className="mt-0.5 text-lg font-bold text-gray-900">{planLabel}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
                <div className="text-xs text-gray-400">Alta</div>
                <div className={`mt-0.5 text-xs font-semibold ${venue.activation_status === "active" ? "text-emerald-600" : "text-amber-600"}`}>
                  {venue.activation_status === "active" ? "Completada" : "Pendiente"}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
                <div className="text-xs text-gray-400">Suscripción</div>
                <div className="mt-0.5">
                  <StatusBadge status={venue.subscription_status} />
                </div>
              </div>
              {sub?.current_period_end && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
                  <div className="text-xs text-gray-400">Próxima renovación</div>
                  <div className="mt-0.5 text-xs font-semibold text-gray-700">{fmtDatetime(sub.current_period_end)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-48 animate-pulse rounded-xl bg-gray-100" />
        </div>
      ) : (
        <>
          {/* Pending invoices */}
          {pending.length > 0 && (
            <section className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50 px-5 py-3">
                <HiOutlineExclamationCircle size={18} className="text-amber-500 shrink-0" />
                <div>
                  <h2 className="text-sm font-semibold text-amber-800">
                    Cuotas pendientes de pago
                    <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-700">
                      {pending.length}
                    </span>
                  </h2>
                  <p className="text-xs text-amber-600">Tienes facturas abiertas. Accede a cada una para regularizar el pago.</p>
                </div>
              </div>
              <InvoiceTable rows={pending} emptyText="" />
            </section>
          )}

          {/* Paid invoices */}
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-5 py-3">
              <HiOutlineCheckCircle size={18} className="text-emerald-500 shrink-0" />
              <div>
                <h2 className="text-sm font-semibold text-gray-700">
                  Facturas tramitadas
                  <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-bold text-gray-600">
                    {paid.length}
                  </span>
                </h2>
                <p className="text-xs text-gray-500">Historial completo de pagos realizados a ClubsAgent.</p>
              </div>
            </div>
            <InvoiceTable rows={paid} emptyText="Sin facturas tramitadas todavía." />
          </section>

          {/* No Stripe customer */}
          {!data?.venue?.subscription_status && pending.length === 0 && paid.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
              <HiOutlineCreditCard size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">No hay datos de facturación disponibles.</p>
              <p className="mt-1 text-xs text-gray-400">
                Activa tu suscripción desde <strong>Mis Tiendas</strong> para comenzar.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
